import { Box, Container, Grid, Stack, Typography } from '@mui/material';
import React from 'react';

const panels = [
  {
    overline: 'The foundation',
    accentColor: '#135c72',
    title: 'Engineering relies on computational meshes.',
    body: 'Structured and unstructured meshes are the backbone of physics-based simulation. They enable accurate prediction of structural, thermal, and fluid behavior — and underpin every design, manufacturing, and sustainment decision that depends on numerical analysis.',
    image: { src: '/use-cases/pbs_fem.png', alt: 'Structural hexahedral mesh' },
  },
  {
    overline: 'The problem',
    accentColor: '#ba6b2e',
    title: 'Meshing is a persistent engineering bottleneck.',
    body: 'Creating high-quality meshes is time-consuming, labor-intensive, and requires specialized expertise. CAD-based meshing is brittle — small geometry changes cause failures, forcing manual repair. The result is reduced design exploration, conservative margins, and a delayed path from measurement to physics-based decision.',
    image: { src: '/use-cases/mesh_error.png', alt: 'Mesh generation failure' },
  },
  {
    overline: 'The solution',
    accentColor: '#135c72',
    title: 'FEMORPH resolves meshing challenges.',
    body: 'FEMORPH transforms rather than rebuilds. A seed mesh is morphed to match new geometry — CMM scans, STL data, or CAD — while preserving topology and element quality. Target surface, blend repair, and arbitrary shape morphing cover the full range of engineering needs, from as-manufactured modeling to free-form design exploration.',
    image: { src: '/use-cases/blender_pbs_3.png', alt: 'FEMORPH mesh morphing result' },
  },
];

const ProblemSolutionRow: React.FC = () => {
  return (
    <Box component="section" sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
      <Container maxWidth="xl" sx={{ py: { xs: 6, md: 9 } }}>
        <Typography variant="overline" color="secondary.dark">The challenge</Typography>
        <Typography variant="h3" sx={{ mt: 1, mb: 5, maxWidth: 700 }}>
          Why mesh morphing matters.
        </Typography>

        <Grid container spacing={3}>
          {panels.map((panel) => (
            <Grid item xs={12} md={4} key={panel.title}>
              <Stack
                sx={{
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderTop: '3px solid',
                  borderTopColor: panel.accentColor,
                }}
              >
                {/* Image */}
                <Box
                  component="img"
                  src={panel.image.src}
                  alt={panel.image.alt}
                  sx={{
                    width: '100%',
                    height: 180,
                    objectFit: 'contain',
                    objectPosition: 'center',
                    bgcolor: '#eef3f1',
                    p: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                />

                {/* Text */}
                <Stack sx={{ p: 3, flexGrow: 1, gap: 1.5 }}>
                  <Typography
                    variant="overline"
                    sx={{ color: panel.accentColor, fontWeight: 700 }}
                  >
                    {panel.overline}
                  </Typography>
                  <Typography variant="h5" sx={{ lineHeight: 1.3 }}>
                    {panel.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                    {panel.body}
                  </Typography>
                </Stack>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default ProblemSolutionRow;
